import { useState, useEffect, useRef } from 'react';
import { useMarketDataContext } from '../../context/MarketDataContext.utils';
import { SearchIcon, XIcon } from '../primitives/Icon';
import styles from './SearchBar.module.css';

interface Props {
  placeholder?: string;
  onFocus?: () => void;
  autoFocus?: boolean;
  /** Omit the outer padding — caller controls spacing. */
  bare?: boolean;
  /** Called once when the input transitions from empty to non-empty. */
  onSearchStart?: () => void;
}

export function SearchBar({
  placeholder = 'Find a vendor or product…',
  onFocus,
  autoFocus,
  bare = false,
  onSearchStart,
}: Props) {
  const { state, setSearch } = useMarketDataContext();
  const [localValue, setLocalValue] = useState(state.filters.search);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setSearch(localValue), 200);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [localValue, setSearch]);

  // External-to-local sync: when the search filter is reset by something other
  // than this input (e.g. tab change, "clear filters"), pull the new value in.
  // We intentionally don't depend on localValue — comparing avoids the loop.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional one-way external-to-local sync
    if (state.filters.search !== localValue) setLocalValue(state.filters.search);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.filters.search]);

  return (
    <div className={`${styles.wrapper} ${bare ? styles.bare : ''}`}>
      <div className={styles.pill}>
        <SearchIcon size={18} color="var(--color-text-primary)" />
        <input
          autoFocus={autoFocus}
          type="text"
          className={styles.input}
          placeholder={placeholder}
          value={localValue}
          enterKeyHint="search"
          autoComplete="off"
          autoCapitalize="none"
          autoCorrect="off"
          onChange={(e) => {
            const next = e.target.value;
            if (!localValue && next) onSearchStart?.();
            setLocalValue(next);
          }}
          onFocus={onFocus}
          onKeyDown={(e) => {
            // Results filter live as you type — Enter just dismisses the
            // on-screen keyboard so the list underneath is visible.
            if (e.key === 'Enter') e.currentTarget.blur();
          }}
        />
        {localValue && (
          <button
            type="button"
            className={styles.clearBtn}
            onClick={() => setLocalValue('')}
            aria-label="Clear search"
          >
            <XIcon size={16} color="var(--color-text-primary)" />
          </button>
        )}
      </div>
    </div>
  );
}
